import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Upload, FileText, AlertTriangle, ExternalLink, RefreshCw, ScanLine, Loader2, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { STATUS_LABEL } from "./FreelanceMyProfile";

// Iter-31: when CRA is approved with 0 JH worked, no invoice is allowed for that month.
const EXTRA_STATUS = {
  NO_BILLABLE_HOURS: { label: "0 MD validated · no invoice", color: "bg-zinc-100 text-zinc-600" },
};
const STATUS_LABEL_FULL = { ...STATUS_LABEL, ...EXTRA_STATUS };

export default function FreelanceMyInvoices() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [activeLine, setActiveLine] = useState(null);
  const [form, setForm] = useState({ invoice_number: "", amount_ht: "", amount_tva: "", file: null });
  const [submitting, setSubmitting] = useState(false);
  // Iter-228x — 2-phase upload UX:
  //   "DROP" → drop-zone + automatic OCR call on file pick
  //   "FORM" → pre-filled editable form, then real submit (Drive upload)
  const [phase, setPhase] = useState("DROP");
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrConfidence, setOcrConfidence] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Iter-210 — Hide future + current-month invoice lines for freelances.
  // Rule: a month line is exposed only AFTER its last day has passed. So
  // the previous month appears on the 1st of the next month. Server still
  // returns the full year; we filter client-side to keep the API generic
  // and avoid leaking that a CRA might be partially logged for an
  // unfinished month.
  const visibleLines = useMemo(() => {
    const today = new Date();
    const curY = today.getFullYear();
    const curM = today.getMonth() + 1; // 1..12
    return lines.filter(
      (l) => l.year < curY || (l.year === curY && l.month < curM),
    );
  }, [lines]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/freelance/me/invoices?year=${year}`);
      setLines(r.data);
    } catch (e) { toast.error("Load error"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const openUpload = (line) => {
    setActiveLine(line);
    setPhase("DROP");
    setOcrConfidence(null);
    setOcrBusy(false);
    setDragOver(false);
    // Iter-30: HT/TVA must be filled MANUALLY by the freelance — defaults to 0 so any forgotten field
    // is rejected by the backend coherence check (HT == approved_jh × tjm).
    // The expected values are still shown in the hint block above as a reference.
    setForm({
      invoice_number: line.invoice?.invoice_number || "",
      amount_ht: line.invoice?.amount_ht ?? 0,
      amount_tva: line.invoice?.amount_tva ?? 0,
      file: null,
    });
    setUploadOpen(true);
  };

  // Iter-228x — handle a file picked via drop-zone OR file input. Runs OCR
  // and transitions to the form phase regardless of OCR success (failure
  // just means an empty form, the freelance still fills it in manually).
  const handleFilePicked = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("application/pdf") && !file.type.startsWith("image/")) {
      toast.error("Format non supporté — PDF ou image uniquement");
      return;
    }
    setForm((f) => ({ ...f, file }));
    if (!activeLine) return;
    setOcrBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/freelance/me/invoices/${activeLine.year}/${activeLine.month}/ocr`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 },
      );
      const d = r.data || {};
      setForm((f) => ({
        ...f,
        invoice_number: d.invoice_number || f.invoice_number,
        amount_ht: d.amount_ht || f.amount_ht,
        amount_tva: d.amount_tva || f.amount_tva,
      }));
      setOcrConfidence(typeof d.confidence === "number" ? d.confidence : null);
      toast.success(
        `Document analysé${typeof d.confidence === "number" ? ` · confiance ${Math.round(d.confidence * 100)}%` : ""} — vérifiez puis soumettez.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OCR indisponible — saisissez manuellement");
    } finally {
      setOcrBusy(false);
      setPhase("FORM");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFilePicked(f);
  };

  const submitInvoice = async () => {
    if (!form.file) return toast.error("Choisissez un fichier PDF");
    if (!form.invoice_number.trim()) return toast.error("Invoice number required");
    const ht = parseFloat(form.amount_ht);
    const tva = parseFloat(form.amount_tva);
    if (!(ht > 0)) return toast.error("Invalid pre-tax amount");
    if (!(tva >= 0)) return toast.error("Invalid VAT amount");
    setSubmitting(true);
    const fd = new FormData();
    fd.append("file", form.file);
    fd.append("invoice_number", form.invoice_number.trim());
    fd.append("amount_ht", String(ht));
    fd.append("amount_tva", String(tva));
    try {
      await api.post(`/freelance/me/invoices/${activeLine.year}/${activeLine.month}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Invoice submitted — awaiting validation");
      setUploadOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error upload");
    } finally { setSubmitting(false); }
  };

  const ttc = useMemo(() => {
    const ht = parseFloat(form.amount_ht); const tva = parseFloat(form.amount_tva);
    if (Number.isFinite(ht) && Number.isFinite(tva)) return (ht + tva).toFixed(2);
    return "—";
  }, [form.amount_ht, form.amount_tva]);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1200px]">
      <div className="text-[11px] uppercase tracking-[0.25em] text-brand-navy-500 mb-3 font-mono">Freelance</div>
      <div className="flex flex-wrap gap-3 items-baseline justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">Mes factures</h1>
        <div className="flex items-center gap-3">
          <Input
            type="number" min={2020} max={2050} value={year}
            onChange={(e) => setYear(parseInt(e.target.value || new Date().getFullYear()))}
            className="w-24" data-testid="my-invoices-year"
          />
          <button onClick={load} title="Actualiser" data-testid="my-invoices-refresh" className="p-2 rounded-md hover:bg-brand-cream-200">
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      <div className="bg-white border border-brand-navy/15 rounded-lg md:overflow-x-auto md:overflow-hidden">
        <div className="hidden md:grid md:grid-cols-12 md:min-w-[760px] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-brand-navy/10 font-mono">
          <div className="col-span-3">Month</div>
          <div className="col-span-3">Statut</div>
          <div className="col-span-2">N° facture</div>
          <div className="col-span-2">Montants HT / TTC</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
        {loading && <div className="px-5 py-8 text-center text-sm text-zinc-500">Loading…</div>}
        {!loading && visibleLines.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-zinc-500" data-testid="my-invoices-empty">
            None ligne pour l&apos;année {year} (vérifiez vos dates de contrat).
          </div>
        )}
        {visibleLines.map((l) => {
          const meta = STATUS_LABEL_FULL[l.status] || { label: l.status, color: "bg-zinc-100 text-zinc-700" };
          const inv = l.invoice;
          return (
            <div key={`${l.year}-${l.month}`} data-testid={`my-line-${l.year}-${l.month}`} className="flex flex-col gap-2 md:grid md:grid-cols-12 md:items-center md:min-w-[760px] md:gap-0 px-5 py-4 border-b border-brand-navy/10 last:border-b-0 hover:bg-brand-cream-50">
              <div className="col-span-3 font-medium" data-testid={`my-line-label-${l.year}-${l.month}`}>Facture du mois — {l.label}</div>
              <div className="col-span-3">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-mono ${meta.color}`} data-testid={`my-line-status-${l.year}-${l.month}`}>
                  {meta.label}
                </span>
                {l.status === "INVOICE_REJECTED" && inv?.rejection_reason && (
                  <div className="text-[11px] text-red-700 mt-1 font-mono" data-testid={`my-line-rejection-${l.year}-${l.month}`}>
                    Motif : {inv.rejection_reason}
                  </div>
                )}
              </div>
              <div className="col-span-2 text-sm text-zinc-700">{inv?.invoice_number || "—"}</div>
              <div className="col-span-2 text-sm font-mono">
                {inv?.amount_ht ? (
                  <div className="leading-tight">
                    <div data-testid={`my-line-ht-${l.year}-${l.month}`}>
                      <span className="text-[10px] uppercase tracking-wider text-zinc-400 mr-1">HT</span>
                      {Number(inv.amount_ht).toLocaleString()} {inv.currency}
                    </div>
                    <div className="text-zinc-600" data-testid={`my-line-ttc-${l.year}-${l.month}`}>
                      <span className="text-[10px] uppercase tracking-wider text-zinc-400 mr-1">TTC</span>
                      {Number(inv.amount_ttc ?? ((Number(inv.amount_ht) || 0) + (Number(inv.amount_tva) || 0))).toLocaleString()} {inv.currency}
                    </div>
                  </div>
                ) : "—"}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-2">
                {inv?.drive_url && (
                  <a href={inv.drive_url} target="_blank" rel="noreferrer" className="p-2 rounded-md hover:bg-brand-cream-200" title="View PDF">
                    <ExternalLink size={14} />
                  </a>
                )}
                <button
                  onClick={() => openUpload(l)}
                  disabled={!l.actionable}
                  data-testid={`my-line-action-${l.year}-${l.month}`}
                  className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-xs font-semibold ${
                    l.actionable
                      ? "bg-brand-navy text-white hover:bg-brand-navy-600"
                      : "bg-zinc-100 text-zinc-400 cursor-not-allowed"
                  }`}
                  title={
                    l.status === "NO_BILLABLE_HOURS"
                      ? "0 MD validated this month — no invoice can be submitted."
                      : l.actionable
                        ? "Soumettre/remplacer la facture"
                        : "Disabled until the client invoice is generated"
                  }
                >
                  <Upload size={12} /> {l.status === "INVOICE_REJECTED" ? "Re-uploader" : "Soumettre"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent data-testid="upload-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Soumettre ma facture — {activeLine?.label}</DialogTitle>
            <DialogDescription>
              {phase === "DROP"
                ? "Déposez votre facture (PDF ou image) — l'OCR pré-remplira le formulaire ci-dessous."
                : "Une fois soumise, la ligne est verrouillée jusqu'à validation par le CEO ou le gestionnaire."}
            </DialogDescription>
          </DialogHeader>

          {phase === "DROP" ? (
            <div className="py-4">
              {/* Iter-228x — drag-drop zone + click-to-pick. OCR runs the
                  moment a file is added; phase flips to FORM after. */}
              <label
                htmlFor="freelance-invoice-dropzone-input"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                data-testid="upload-invoice-dropzone"
                className={`block border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors
                  ${dragOver ? "border-brand-navy bg-brand-cream/60" : "border-zinc-300 bg-zinc-50 hover:bg-zinc-100"}
                  ${ocrBusy ? "pointer-events-none opacity-70" : ""}`}
              >
                {ocrBusy ? (
                  <>
                    <Loader2 size={28} className="mx-auto text-brand-navy animate-spin" />
                    <div className="mt-3 text-sm font-medium text-brand-navy">Analyse OCR en cours…</div>
                    <div className="mt-1 text-[11px] text-zinc-500">
                      Gemini extrait le numéro de facture et les montants. Quelques secondes…
                    </div>
                  </>
                ) : (
                  <>
                    <ScanLine size={28} className="mx-auto text-zinc-500" />
                    <div className="mt-3 text-sm font-medium text-zinc-800">
                      Glissez-déposez votre facture ici
                    </div>
                    <div className="mt-1 text-[11px] text-zinc-500">
                      ou cliquez pour parcourir — PDF ou image (JPG/PNG)
                    </div>
                  </>
                )}
                <input
                  id="freelance-invoice-dropzone-input"
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  data-testid="upload-invoice-file"
                  onChange={(e) => handleFilePicked(e.target.files?.[0] || null)}
                />
              </label>
              <button
                type="button"
                onClick={() => { setPhase("FORM"); }}
                data-testid="upload-invoice-skip-ocr"
                className="mt-3 text-[11px] text-zinc-500 hover:text-zinc-800 underline"
              >
                Saisir manuellement sans OCR
              </button>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {activeLine && (
                <div className="bg-brand-cream/40 border border-brand-navy/15 rounded-md p-3 text-xs space-y-1" data-testid="upload-expected-hint">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-brand-navy-500 font-mono mb-1">JH validateds × TJM</div>
                  <div className="flex justify-between"><span className="text-zinc-600">Validated MD on CRA:</span><span className="font-mono">{activeLine.approved_jh ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-600">HT attendu :</span><span className="font-mono font-semibold text-brand-navy">{activeLine.expected_ht ?? 0} {activeLine.currency || ""}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-600">Expected VAT ({Math.round((activeLine.vat_rate || 0) * 100)}%) :</span><span className="font-mono font-semibold text-brand-navy">{activeLine.expected_tva ?? 0} {activeLine.currency || ""}</span></div>
                  <div className="flex justify-between border-t border-brand-navy/10 pt-1 mt-1"><span className="text-zinc-600">TTC attendu :</span><span className="font-mono font-bold">{activeLine.expected_ttc ?? 0} {activeLine.currency || ""}</span></div>
                  <p className="text-[10px] text-zinc-500 mt-2">Entered amounts must match this base exactly (±0.01 tolerance).</p>
                </div>
              )}
              {form.file && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-md text-[11px]" data-testid="upload-invoice-file-pill">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={12} className="text-zinc-500 shrink-0" />
                    <span className="font-mono truncate">{form.file.name}</span>
                    <span className="text-zinc-400 shrink-0">({Math.round(form.file.size / 1024)} Ko)</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {ocrConfidence !== null && (
                      <span
                        className={`px-1.5 py-0.5 rounded font-mono text-[9px] uppercase tracking-wider
                          ${ocrConfidence >= 0.8 ? "bg-emerald-100 text-emerald-800"
                            : ocrConfidence >= 0.5 ? "bg-amber-100 text-amber-800"
                            : "bg-rose-100 text-rose-800"}`}
                        data-testid="upload-invoice-ocr-confidence"
                      >
                        OCR {Math.round(ocrConfidence * 100)}%
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => { setForm({ ...form, file: null }); setPhase("DROP"); setOcrConfidence(null); }}
                      className="text-zinc-400 hover:text-zinc-700"
                      title="Changer de fichier"
                      data-testid="upload-invoice-file-clear"
                    >
                      <XIcon size={12} />
                    </button>
                  </div>
                </div>
              )}
              <div>
                <Label>Invoice number *</Label>
                <Input
                  value={form.invoice_number}
                  onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                  placeholder="ex. FRL-2026-04-001" data-testid="upload-invoice-number"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Montant HT *</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.amount_ht}
                    onChange={(e) => setForm({ ...form, amount_ht: e.target.value })}
                    data-testid="upload-amount-ht"
                  />
                </div>
                <div>
                  <Label>Montant TVA *</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.amount_tva}
                    onChange={(e) => setForm({ ...form, amount_tva: e.target.value })}
                    data-testid="upload-amount-tva"
                  />
                </div>
              </div>
              <div className="text-xs text-zinc-500 font-mono">Computed total incl. tax: <strong className="text-brand-navy">{ttc}</strong></div>
              {!form.file && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-center gap-2">
                  <AlertTriangle size={12} />
                  Aucun fichier sélectionné — <button type="button" onClick={() => setPhase("DROP")} className="underline">retourner à l&apos;upload</button>.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <button onClick={() => setUploadOpen(false)} className="px-4 h-10 text-sm rounded-md hover:bg-zinc-100">Cancel</button>
            {phase === "FORM" && (
              <button
                onClick={submitInvoice}
                disabled={submitting || !form.file}
                data-testid="upload-invoice-submit"
                className="inline-flex items-center gap-2 px-4 h-10 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600 disabled:opacity-50"
              >
                <Upload size={14} /> {submitting ? "Sending…" : "Soumettre"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
